/**
 * i18n is set up once here and reused by both SPAs. Each app passes its own
 * namespace bundles at init, so the login app never ships the console's
 * strings and vice versa.
 */
import i18next, { type i18n, type Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import commonEn from './locales/en.json';
import commonHe from './locales/he.json';

/** Locale is a language Rootsight ships translations for. */
export const locales = ['en', 'he'] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'en';

/** Locales written right to left, used to set the document direction. */
const rtlLocales = new Set<Locale>(['he']);

export interface LocaleMeta {
  code: Locale;
  /** Name of the language in that language, for the switcher. */
  nativeName: string;
  dir: 'ltr' | 'rtl';
}

export const localeMeta: Record<Locale, LocaleMeta> = {
  en: { code: 'en', nativeName: 'English', dir: 'ltr' },
  he: { code: 'he', nativeName: 'עברית', dir: 'rtl' },
};

export function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

export function directionFor(locale: string): 'ltr' | 'rtl' {
  return isLocale(locale) && rtlLocales.has(locale) ? 'rtl' : 'ltr';
}

/** Translations the app contributes, keyed by locale then namespace. */
export type AppResources = Partial<Record<Locale, Resource>>;

export interface SetupOptions {
  /** App namespace, e.g. "auth" or "console". */
  defaultNS: string;
  resources: AppResources;
  /** Locale from persisted user preference; detection runs when omitted. */
  locale?: Locale | undefined;
}

/**
 * setupI18n initializes i18next with the shared `common` namespace plus the
 * app's own. It returns the instance so the caller can pass it to
 * I18nextProvider rather than relying on module-global state.
 */
export async function setupI18n({ defaultNS, resources, locale }: SetupOptions): Promise<i18n> {
  const merged: Resource = {};
  for (const code of locales) {
    merged[code] = {
      common: code === 'he' ? commonHe : commonEn,
      // Copied: a page's lazy strings are merged into the console namespace
      // when it first renders (see the console's featureStrings), and merging
      // into the imported JSON itself would change it for every instance
      // made afterwards in the same process.
      ...structuredClone(resources[code] ?? {}),
    };
  }

  const instance = i18next.createInstance();
  await instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: merged,
      ...(locale ? { lng: locale } : {}),
      fallbackLng: defaultLocale,
      supportedLngs: [...locales],
      // Fall back from "he-IL" to "he" rather than straight to English.
      load: 'languageOnly',
      defaultNS,
      ns: ['common', defaultNS],
      interpolation: {
        // React escapes for us; double-escaping mangles RTL punctuation.
        escapeValue: false,
      },
      detection: {
        order: ['querystring', 'localStorage', 'navigator'],
        lookupQuerystring: 'lang',
        lookupLocalStorage: 'rootsight.locale',
        caches: [],
      },
    });

  applyDocumentLocale(instance.resolvedLanguage ?? defaultLocale);
  instance.on('languageChanged', applyDocumentLocale);
  return instance;
}

/** applyDocumentLocale keeps <html lang> and <html dir> in step with i18next. */
export function applyDocumentLocale(locale: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = locale;
  document.documentElement.dir = directionFor(locale);
}
